import React, { memo, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { dates, formats, validators } from "investira.sdk";
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  FormControlLabel,
  Typography,
  Switch,
  ListItemText,
  Chip,
} from "investira.react.components";
import { VALIDATIONS } from "../../../const";
import { useRelatorioContext } from "../../../contexts/RelatorioContext";
import services from "../../../services";
import withResponseHandling from "../../../hoc/withResponseHandling";
import withSaveParams from "../../../hoc/withSaveParams";

// 2) carteira
// 1) data
// 3) aberto/fechado

const VALIDATION_SCHEMA = Yup.object().shape({
  carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
  data: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
  aberta: Yup.bool().required(VALIDATIONS.REQUIRED),
});

const StressTest = memo((props) => {
  const { state, actions } = useRelatorioContext();

  const navigate = useNavigate();
  const location = useLocation();

  const storedParams = props.storedParams("stresstest", props.lastOutput);

  const [datas, setDatas] = useState([]);
  const [posicaoAberta, setPosicaoAberta] = useState(false);

  const xInitialValues = {
    carteira_id: "",
    data: storedParams.data || null,
    aberta: storedParams.aberta || false,
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.risco(
        {
          ...pValues,
        },
        (rRes) => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams("stresstest", pValues);
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  const handleSwitchChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const { ...restValues } = xValues;
      formik.setValues({ ...xInitialValues, ...restValues });
    }
  }, []);

  useEffect(() => {
    if (state.isDialogOpen && formik.values.carteira_id !== "") {
      setDatas([]);

      formik.values.carteira_id &&
        services.posicoes.datas(
          {
            sort: "data DESC",
            carteira_id: formik.values.carteira_id,
          },
          (rRes) => {
            const xDates = rRes.data.map((xPosicao) => {
              return {
                value: formats.formatDateCustom(
                  dates.toDate(xPosicao.data),
                  "YYYY-MM-DD"
                ),
                label: formats.formatDateCustom(
                  dates.toDate(xPosicao.data),
                  "DD/MM/YYYY"
                ),
                aberta: xPosicao.aberta,
              };
            });
            setDatas(xDates);

            const xValues = storedParams;

            if (
              !validators.isEmpty(xValues) &&
              formik.values.carteira_id === xValues.carteira_id
            ) {
              const { data, aberta } = xValues;
              formik.setFieldValue("data", data);

              const xAberta = xDates.filter((x) => x.value === data)[0]?.aberta;

              setPosicaoAberta(xAberta);
              formik.setFieldValue("aberta", aberta); // Talvez não seja necessário
            } else {
              formik.setFieldValue("data", xDates[0].value);
              setPosicaoAberta(xDates[0].aberta);
            }
          },
          (rErr) => {
            props.responseErrorHandling(rErr);
          }
        );
    }
  }, [state.isDialogOpen, formik.values.carteira_id]);

  const xCarteiras = useMemo(
    () =>
      props.carteiras
        .slice()
        .sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras]
  );

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ width: "500px", marginBottom: 2, py: 1 }} spacing={2}>
        <Stack flexGrow={1}>
          <TextField
            id="carteira_id"
            name="carteira_id"
            label="Carteira"
            variant="outlined"
            value={formik.values.carteira_id}
            onChange={formik.handleChange}
            select
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="wallet" size={21} />
                </InputAdornment>
              ),
            }}
          >
            {xCarteiras.map((xOption) => (
              <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                {xOption.carteira}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {formik.values.carteira_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data-posicao"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={(e) => {
                      formik.handleChange(e);

                      const xAberta = datas.filter(
                        (x) => x.value === e.target.value
                      )[0].aberta;

                      setPosicaoAberta(xAberta);
                      if (!xAberta) {
                        formik.setFieldValue("aberta", false);
                      }
                    }}
                    sx={{
                      "& .MuiChip-root": {
                        display: "none",
                      },
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}
                  >
                    {datas.map((xOption, xIndex) => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={"Aberta"}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
            <Stack px={1}>
              <FormControlLabel
                control={
                  <Switch
                    id="aberta"
                    checked={formik.values.aberta}
                    onChange={(e, value) => {
                      handleSwitchChange("aberta", value);
                    }}
                    name="aberta"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">Posição aberta</Typography>
                }
                disabled={!posicaoAberta}
              />
            </Stack>
          </>
        )}

        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={2}
        >
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={actions.handleNewConsultCancel}
          >
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              validators.isEmpty(datas) ||
              validators.isEmpty(formik.values.carteira_id)
            }
          >
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(withSaveParams(StressTest));
